/* eslint-disable no-console */
import { isEmptyOrNil } from '@pb/utils'
import { uniq } from 'ramda'

import { TRACKING_EVENTS } from 'App/constants'
import appActions from 'App/duck/actions'
import appSelectors from 'App/duck/selectors'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import { trackEventAdapter } from 'helpers/tracking'

import {
  getArticlesIds,
  sortArticles,
} from '../components/OrderDetailsPage/StyleYourLatestCarousel/helpers'
// eslint-disable-next-line import/no-named-as-default
import requests, { FetchOnlineOrdersParams } from '../requests'
import actions from './actions'
import {
  normalizeOnlineOrders,
  normalizeStoreOrders,
  normalizeOnlineOrder,
  normalizeStoreOrder,
  normalizeStoreAddresses,
} from './normalize'
import selectors from './selectors'
import { OnlineOrders, StoreOrders } from './types'

// ----------------------------------------
// Style your latest A/B test
// ----------------------------------------
export const operationFetchStyleYourLatest = () => async (dispatch, getState) => {
  const userBpid = appSelectors.getUserBpid(getState())
  const savedArticles = selectors.getSYLArticles(getState())

  if (savedArticles?.length > 0) {
    return
  }

  try {
    const recommendationsResponse = await requests.fetchRecommendations(userBpid)
    const sortedRecommendations = getArticlesIds(recommendationsResponse.data)

    if (sortedRecommendations?.length === 0) {
      return
    }

    const articlesResponse = await requests.fetchArticles(sortedRecommendations)
    const articles = articlesResponse?.data?.articles

    if (articles?.length > 0) {
      const sortedArticles = sortArticles(articles, sortedRecommendations)
      dispatch(actions.saveSYLArticles(sortedArticles))
    }
    // eslint-disable-next-line no-empty
  } catch (error) {}
}

// ----------------------------------------
// ORDER DETAILS
// ----------------------------------------

export const operationFetchOnlineOrderDetails = (orderId) => async (dispatch) => {
  dispatch(actions.fetchOnlineOrderDetailsStart())

  try {
    const response = await requests.fetchOnlineOrder(orderId)
    const normalizedResult = normalizeOnlineOrder(response.data)

    dispatch(actions.fetchOnlineOrderDetailsSuccess(normalizedResult))
  } catch (error) {
    const { message } = error
    dispatch(actions.fetchOnlineOrderDetailsFail({ error: message, orderId }))
    trackEventAdapter(
      {
        ...ACCOUNT_TRACKING_EVENTS.onlineOrderDetailsError.data,
        event_label: message,
      },
      'error',
    )
  }
}

export const operationFetchStoreOrderDetails = (orderId) => async (dispatch) => {
  dispatch(actions.fetchStoreOrderDetailsStart(orderId))

  try {
    const response = await requests.fetchStoreOrder(orderId)
    const normalizedResult = normalizeStoreOrder(response.data)
    dispatch(actions.fetchStoreOrderDetailsSuccess(normalizedResult))
  } catch (error) {
    const { message } = error
    dispatch(actions.fetchOnlineOrderDetailsFail({ error: message, orderId }))
    trackEventAdapter(
      {
        ...ACCOUNT_TRACKING_EVENTS.storeOrderDetailsError.data,
        event_label: message,
      },
      'error',
    )
  }
}

export const operationGetOnlineOrderDetailsById = (orderId) => (dispatch, getState) =>
  selectors.getOnlineOrderDetailsById(getState(), orderId)

export const operationGetStoreOrderDetailsById = (orderId) => (dispatch, getState) => {
  return selectors.getStoreOrderDetailsById(getState(), orderId)
}

// ----------------------------------------
// HELPERS
// ----------------------------------------
const setHubOrdersList = (state, dispatch): any => {
  const shouldSetHubOrderList = selectors.getShouldSetHubOrderList(state)

  const combinedOrders = selectors.getCombinedOrderList(state)

  if (shouldSetHubOrderList) {
    dispatch(actions.setHubOrderList(combinedOrders))
  }
}

// ----------------------------------------
// FETCHERS
// ----------------------------------------

export const operationFetchOnlineOrders = () => (dispatch, getState) => {
  const appState = getState()
  const onlineOrders = selectors.getOnlineOrders(appState)
  const { start, end } = selectors.getFetchOnlineIndex(appState)

  dispatch(actions.fetchOnlineOrdersStart())

  requests
    .fetchOnlineOrders({ start, end })
    .then((result: OnlineOrders) => {
      const normalizedResult = normalizeOnlineOrders(result)
      dispatch(actions.fetchOnlineOrdersSuccess(normalizedResult))
      dispatch(actions.setTotalOrdersCount({ type: 'online', count: result.ordersCount }))
      dispatch(actions.setCallRateAndReviewAPIStatus(result.callRateAndReviewAPI))
      setHubOrdersList(getState(), dispatch)

      if (onlineOrders.length + result.orders.length >= result.ordersCount) {
        dispatch(actions.setNoMoreOnlineResults())
      }
    })
    .catch(({ message }) => {
      dispatch(actions.fetchOnlineOrdersFail(message))
      trackEventAdapter(
        {
          ...ACCOUNT_TRACKING_EVENTS.onlineOrdersListError.data,
          event_label: message,
        },
        'error',
      )
    })
}

export const operationFetchOnlineActiveOrders =
  (searchParams?: FetchOnlineOrdersParams) => (dispatch, getState) => {
    const appState = getState()
    const { start, end } = searchParams ?? selectors.getFetchOnlineIndex(appState)

    dispatch(actions.fetchOnlineActiveOrdersStart())

    requests
      .fetchOnlineOrders({ start, end, active: searchParams?.active })
      .then((result: OnlineOrders) => {
        const normalizedResult = normalizeOnlineOrders(result)
        dispatch(actions.fetchOnlineActiveOrdersSuccess(normalizedResult))
        dispatch(actions.setCallRateAndReviewAPIStatus(result.callRateAndReviewAPI))
      })
      .catch(({ message }) => {
        dispatch(actions.fetchOnlineActiveOrdersFail(message))
        trackEventAdapter(
          {
            ...ACCOUNT_TRACKING_EVENTS.onlineOrdersListError.data,
            event_label: message,
          },
          'error',
        )
      })
  }

export const operationFetchStoreOrdersAddresses = (storeOrders) => (dispatch, getState) => {
  const storeIds = uniq(storeOrders.map((storeOrder) => storeOrder.storeId))
  dispatch(actions.fetchStoreOrdersAddressesStart())

  requests
    .fetchStoreOrdersAddresses(storeIds)
    .then((data) => {
      const normalizedStoreAddresses = normalizeStoreAddresses(data.stores)
      const isAddressesEmpty = !Object.keys(normalizedStoreAddresses).length

      if (isAddressesEmpty) {
        const error = new Error('No addresses available')
        dispatch(actions.fetchStoreOrdersAddressesFail(error))
      } else {
        dispatch(actions.fetchStoreOrdersAddressesSuccess(normalizedStoreAddresses))
      }

      setHubOrdersList(getState(), dispatch)
    })
    .catch((err) => {
      dispatch(actions.fetchStoreOrdersAddressesFail(err))
    })
}

export const operationFetchStoreOrders = () => (dispatch, getState) => {
  const STORE_LOAD_TIMEOUT = 6000
  const appState = getState()
  const isOnlineOrdersLoadingFailed = selectors.getIsOnlineOrdersLoadingFailed(appState)
  const { start, end } = selectors.getFetchStoreIndex(appState)
  const storeOrders = selectors.getStoreOrders(appState)

  let shouldReactWhenLoaded = true
  let isRequestDone = false

  dispatch(actions.fetchStoreOrdersStart())

  requests
    .fetchStoreOrders(start, end)
    .then((result: StoreOrders) => {
      if (shouldReactWhenLoaded) {
        const hasStoreOrders = !isEmptyOrNil(result)

        if (hasStoreOrders) {
          const normalizedStoreOrders = normalizeStoreOrders(result)
          dispatch(actions.fetchStoreOrdersSuccess(normalizedStoreOrders))
          dispatch(actions.setTotalOrdersCount({ type: 'store', count: result.receiptsCount }))
          dispatch(operationFetchStoreOrdersAddresses(normalizedStoreOrders))

          setHubOrdersList(getState(), dispatch)

          if (result.receipts.length + storeOrders.length >= result.receiptsCount) {
            dispatch(actions.setNoMoreStoreResults())
          }
        } else {
          dispatch(actions.fetchStoreOrdersSuccess(storeOrders))
        }

        isRequestDone = true
      }
    })
    .catch(({ message }) => {
      dispatch(actions.fetchStoreOrdersFail(message))
      setHubOrdersList(getState(), dispatch)
      isRequestDone = true
      trackEventAdapter(
        {
          ...ACCOUNT_TRACKING_EVENTS.storeOrdersListError.data,
          event_label: message,
        },
        'error',
      )
    })

  // TODO: isOnlineOrdersLoadingFailed will get true after this code runs if it fails
  // so this check doesn't work ATM
  // The idea is that if the online orders fail to load, there is no point in timing out
  // the store orders. Let it load if there is noting else to display...
  if (!isOnlineOrdersLoadingFailed) {
    setTimeout(() => {
      if (!isRequestDone) {
        dispatch(actions.fetchStoreOrdersTimeout())
        shouldReactWhenLoaded = false
      }
    }, STORE_LOAD_TIMEOUT)
  }
}

export const operationFetchOrders = () => async (dispatch, getState) => {
  const appState = getState()
  const { start, end } = selectors.getFetchOnlineIndex(appState)
  const onlineOrders = selectors.getOnlineOrders(appState)
  const storeOrders = selectors.getStoreOrders(appState)

  dispatch(actions.fetchOnlineOrdersStart())
  dispatch(actions.fetchStoreOrdersStart())

  return Promise.all([
    requests.fetchOnlineOrders({ start, end }),
    requests.fetchStoreOrders(start, end),
  ])
    .then(([onlineOrdersResult, storeOrdersResult]) => {
      //  process online orders
      const normalizedResult = normalizeOnlineOrders(onlineOrdersResult)
      dispatch(actions.fetchOnlineOrdersSuccess(normalizedResult))
      dispatch(
        actions.setTotalOrdersCount({ type: 'online', count: onlineOrdersResult.ordersCount }),
      )
      dispatch(actions.setCallRateAndReviewAPIStatus(onlineOrdersResult.callRateAndReviewAPI))
      if (
        onlineOrders.length + onlineOrdersResult.orders.length >=
        onlineOrdersResult.ordersCount
      ) {
        dispatch(actions.setNoMoreOnlineResults())
      }
      // process store orders
      const hasStoreOrders = !isEmptyOrNil(storeOrdersResult)
      if (hasStoreOrders) {
        const normalizedStoreOrders = normalizeStoreOrders(storeOrdersResult)
        dispatch(actions.fetchStoreOrdersSuccess(normalizedStoreOrders))
        dispatch(
          actions.setTotalOrdersCount({ type: 'store', count: storeOrdersResult.receiptsCount }),
        )
        dispatch(operationFetchStoreOrdersAddresses(normalizedStoreOrders))

        if (
          storeOrdersResult.receipts.length + storeOrders.length >=
          storeOrdersResult.receiptsCount
        ) {
          dispatch(actions.setNoMoreStoreResults())
        }
      } else {
        dispatch(actions.fetchStoreOrdersSuccess(storeOrders))
      }

      setHubOrdersList(getState(), dispatch)
    })
    .catch(({ message }) => {
      dispatch(actions.fetchOnlineOrdersFail(message))
      dispatch(actions.fetchStoreOrdersFail(message))
      trackEventAdapter(
        {
          ...ACCOUNT_TRACKING_EVENTS.onlineOrdersListError.data,
          event_label: message,
        },
        'error',
      )

      setHubOrdersList(getState(), dispatch)
      trackEventAdapter(
        {
          ...ACCOUNT_TRACKING_EVENTS.storeOrdersListError.data,
          event_label: message,
        },
        'error',
      )
    })
}

export const operationCancelOnlineOrder = (orderId) => async (dispatch) => {
  dispatch(actions.cancelOnlineOrderStart())

  try {
    const response = await requests.cancelOnlineOrder(orderId)
    if (response.status === 200) {
      dispatch(actions.cancelOnlineOrderSuccess(orderId))
      dispatch(actions.updateCancelledOnlineOrder(orderId))
      dispatch(
        appActions.trackEvent({
          id: TRACKING_EVENTS.clickOrderCancellationConfirmation.id,
        }),
      )
    } else {
      dispatch(actions.cancelOnlineOrderFail(response.statusText))
      dispatch(
        appActions.trackEvent({
          error: response.status,
          id: TRACKING_EVENTS.orderCancellationError.id,
        }),
      )
    }
  } catch (error) {
    dispatch(actions.cancelOnlineOrderFail(error.message))
    dispatch(
      appActions.trackEvent({
        id: TRACKING_EVENTS.orderCancellationError.id,
      }),
    )
  }
}

export const operationAddToBag = (productData) => async (dispatch) => {
  dispatch(actions.addToBagStart())
  dispatch(actions.showAddToBagVerificationStart())

  try {
    const response = await requests.addToBag(productData?.quantity, productData?.product?.code)
    dispatch(actions.addToBagSuccess(response))
    dispatch(actions.showAddToBagVerificationSuccess(productData))
    dispatch(actions.setShouldShowAddToBagVerification(false))
  } catch (error) {
    dispatch(actions.addToBagFail())
    dispatch(actions.showAddToBagVerificationError())
    dispatch(actions.setShouldShowAddToBagVerification(false))
  }
}
